import { useEffect, useMemo, useRef, useState } from 'react';
import { useMytaverseEvent } from '../../../../providers';
import { useMytaStreams } from '../MytaStreamsProvider';
import { CINEMA_PHASES, PROGRESS_TICK_MS, TOTAL_ETA_SEC } from './constants';

export interface ICinemaError {
  headline: string;
  reason: string;
}

export const useCinemaProgress = () => {
  const {
    leaseStatus,
    leasePhase,
    dataChannelOpened,
    mytaLoading,
    connectedSlot,
  } = useMytaStreams();
  const { currentRoom } = useMytaverseEvent();

  const ready = dataChannelOpened && !mytaLoading && !!currentRoom;

  const phaseIdx = useMemo(() => {
    if (ready) {
      return 4;
    }

    if (dataChannelOpened) {
      return 3;
    }

    if (connectedSlot) {
      return 2;
    }

    if (leaseStatus === 'active') {
      return 1;
    }

    return 0;
  }, [ready, dataChannelOpened, connectedSlot, leaseStatus]);

  const error: ICinemaError | null = useMemo(() => {
    if (leaseStatus === 'error' || leaseStatus === 'unconfigured') {
      return {
        headline: 'We couldn’t start your experience',
        reason: 'Something went wrong while preparing your session',
      };
    }

    if (leasePhase?.phase === 'build_unsupported') {
      return {
        headline: 'This experience is temporarily unavailable',
        reason: 'It cannot start right now — please try again later',
      };
    }

    return null;
  }, [leaseStatus, leasePhase?.phase]);

  const hintOverride = useMemo(() => {
    if (leasePhase?.phase === 'already_seated') {
      return 'You are already in this experience in another tab — close it and this one will connect automatically.';
    }

    return null;
  }, [leasePhase?.phase]);

  const percentRef = useRef(0);
  const etaRef = useRef(TOTAL_ETA_SEC);
  const [percent, setPercent] = useState(0);
  const [etaSec, setEtaSec] = useState(TOTAL_ETA_SEC);

  useEffect(() => {
    if (error) {
      return;
    }

    const tick = () => {
      const phase = CINEMA_PHASES[phaseIdx];
      const nextThreshold = CINEMA_PHASES[phaseIdx + 1]?.threshold ?? 100;
      let value = percentRef.current;

      if (value < phase.threshold) {
        value = Math.min(
          phase.threshold,
          value + (phase.threshold - value) * 0.35 + 0.4,
        );
      } else if (ready) {
        value += (100 - value) * 0.5;
        if (value > 99.5) {
          value = 100;
        }
      } else {
        const ceiling = nextThreshold - 0.5;
        const tau = Math.max(4, phase.expectedSec / 3);
        value += (ceiling - value) * (PROGRESS_TICK_MS / 1000 / tau);
      }

      value = Math.min(100, Math.max(percentRef.current, value));
      percentRef.current = value;

      const remaining = ((100 - value) / 100) * TOTAL_ETA_SEC;
      etaRef.current = Math.min(etaRef.current, remaining);

      setPercent(value);
      setEtaSec(Math.max(0, Math.round(etaRef.current)));
    };

    tick();
    const interval = setInterval(tick, PROGRESS_TICK_MS);

    return () => clearInterval(interval);
  }, [phaseIdx, ready, error]);

  const done = ready && percent >= 100;

  return {
    percent,
    phaseIdx,
    ready,
    done,
    etaSec: done ? 0 : etaSec,
    error,
    hintOverride,
  };
};
